import React, { useState, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { MessageCircle, X, Send } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import SecurityUtils from '../../utils/security';
import Avatar from '../../components/common/Avatar';

const MessageModal = ({ onClose, recipientId, initialMessage = '' }) => {
  const { currentUser, conversations, setConversations, messages, setMessages, users } = useContext(AppContext);
  const [newMessage, setNewMessage] = useState(initialMessage);

  const recipient = users.find(u => u.id === recipientId);

  // Find or create DM conversation with recipient (exclude group/event chats)
  let conversation = conversations.find(conv =>
    conv.participants.includes(currentUser.id) && conv.participants.includes(recipientId) &&
    !conv.linkedGroupId && !conv.linkedEventId && !conv.isGroup
  );

  // Get messages for this conversation
  const conversationMessages = conversation
    ? messages.filter(m => m.conversationId === conversation.id)
    : [];

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    const sanitizedMessage = SecurityUtils.sanitizeText(newMessage);

    try {
      if (!conversation) {
        // Create conversation in Firestore
        const convData = {
          participants: [currentUser.id, recipientId],
          lastMessage: sanitizedMessage,
          lastMessageTime: new Date().toISOString()
        };
        conversation = await firestoreService.createConversation(convData);
        setConversations(prev => [...prev, conversation]);
      }

      await firestoreService.sendMessage({
        conversationId: conversation.id,
        senderId: currentUser.id,
        content: sanitizedMessage,
      });

      // Optimistic local update
      setMessages(prev => [...prev, {
        id: `m${Date.now()}`,
        conversationId: conversation.id,
        senderId: currentUser.id,
        content: sanitizedMessage,
        timestamp: new Date().toISOString(),
        read: false,
      }]);
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-end sm:items-center justify-center z-50 sm:p-4 overflow-hidden">
      <div className="bg-black sm:border sm:border-gray-700 sm:rounded-lg max-w-2xl w-full sm:h-[600px] flex flex-col overflow-hidden" style={{ height: 'var(--app-height, 100vh)', maxHeight: 'var(--app-height, 100vh)' }}>
        {/* Header */}
        <div className="border-b border-gray-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3 min-w-0">
            <Avatar user={recipient} size="md" />
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-white truncate">{recipient?.name}</h2>
              <p className="text-sm text-white truncate">@{recipient?.handle}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-400 flex-shrink-0 ml-2">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
          {conversationMessages.length === 0 ? (
            <div className="text-center text-white py-8">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="text-sm">Start a conversation with {recipient?.name}</p>
            </div>
          ) : (
            conversationMessages.map(msg => {
              const isMine = msg.senderId === currentUser.id;
              return (
                <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] ${isMine ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-700 text-white'} rounded-lg p-3`}>
                    <p className="text-sm break-words">{msg.content}</p>
                    <p className="text-xs mt-1 text-gray-300">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Input */}
        <div className="border-t border-gray-700 p-3 flex-shrink-0">
          <div className="flex space-x-2 overflow-hidden">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              className="flex-1 min-w-0 px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
              autoFocus
            />
            <button
              onClick={handleSendMessage}
              className="px-3 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] flex items-center space-x-2"
            >
              <Send className="w-4 h-4" />
              <span>Send</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


export default MessageModal;
